import orElse from 'volta/helpers/or-else';
import CreateOption from './create-option';
import BvSelectCreateSingleTrigger from './single-trigger';
import { action,get } from '@ember/object';
import { service } from '@ember/service';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import InfinityModel from 'ember-infinity/lib/infinity-model';
import { defaultMatcher,filterOptions } from 'ember-power-select/utils/group-utils';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvSelectComponent from 'volta/components/bv-select';
import BvSelectMultiComponent from 'volta/components/bv-select-multi';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { isThenable } from 'volta/utils/object-utils';

import type { IntlService } from 'ember-intl';
import type { Select } from 'ember-power-select/components/power-select';
import type { MatcherFn } from 'ember-power-select/utils/group-utils';
import type { BvSelectArgs } from 'volta/components/bv-select';
import type { BvSelectMultiArgs } from 'volta/components/bv-select-multi';
import type BaseModel from 'volta/models/base-model';
import type { TError } from 'volta/utils/error-utils';

export type SuggestionOption = {
  __isSuggestion__: boolean;
  __value__: string;
  text: string;
};
export type DataOption = string | Record<string, unknown> | BaseModel;
export type SelectedOption<O = DataOption> = SuggestionOption | O;

export interface BvSelectCreateSignature<O = DataOption> {
  Args: (BvSelectArgs | BvSelectMultiArgs) & {
    value?: string | null;
    multiple?: boolean;
    matcher?: MatcherFn;
    infiniteScroll?: boolean;
    onChange: (selected: SelectedOption<O> | SelectedOption<O>[], select: Select, e: Event) => void;
    onCreate?: (term: string | null | undefined, select: Select, event: Event) => void;
    onKeydown?: (select: Select, event: KeyboardEvent) => void;
    search?: (term: string, select: Select) => O[] | InfinityModel;
    buildSuggestion?: (term: string) => string;
    showCreateWhen?: (term: string, options: O[]) => boolean;
    options?: O[] | Promise<O[]>;
    showCreatePosition?: 'top' | 'bottom';
    renderAll?: boolean;
    triggerAutoFocus?: boolean;
    inputClasses?: string;
    verticalPosition?: 'auto' | 'above' | 'below';
    horizontalPosition?:
      | 'auto'
      | 'right'
      | 'left'
      | 'center'
      | 'auto-right'
      | 'auto-left'
      | 'auto-left';
    registerAPI?: (select: Select) => void;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [any, Select];
  };
}

export default class BvSelectCreate<O = DataOption> extends Component<BvSelectCreateSignature<O>> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    <this.selectComponent
      @allowClear={{@allowClear}}
      @ariaDescribedBy={{@ariaDescribedBy}}
      @ariaInvalid={{@ariaInvalid}}
      @ariaLabel={{@ariaLabel}}
      @ariaLabelledBy={{@ariaLabelledBy}}
      @buildSelection={{@buildSelection}}
      @calculatePosition={{@calculatePosition}}
      @closeOnSelect={{@closeOnSelect}}
      @defaultHighlighted={{@defaultHighlighted}}
      @destination={{@destination}}
      @disabled={{@disabled}}
      @dropdownClass={{@dropdownClass}}
      @dropdownSize={{@dropdownSize}}
      @error={{@error}}
      @extra={{@extra}}
      @fill={{@fill}}
      @groupComponent={{@groupComponent}}
      @helpText={{@helpText}}
      @highlightOnHover={{@highlightOnHover}}
      @horizontalPosition={{@horizontalPosition}}
      {{! @glint-ignore -- @iconLeft only available on bv-select}}
      @iconLeft={{@iconLeft}}
      @infiniteScroll={{@infiniteScroll}}
      @initiallyOpened={{@initiallyOpened}}
      @isEdited={{@isEdited}}
      @isLoading={{@isLoading}}
      @isRequired={{@isRequired}}
      @label={{@label}}
      @labelAction={{@labelAction}}
      @labelClass={{@labelClass}}
      @labelHidden={{@labelHidden}}
      @labelInfo={{@labelInfo}}
      @labelInline={{@labelInline}}
      @labelWrapperClass={{@labelWrapperClass}}
      @loadingMessage={{@loadingMessage}}
      @matchTriggerWidth={{@matchTriggerWidth}}
      @matcher={{this.matcher}}
      @noMatchesMessage={{@noMatchesMessage}}
      @showBeforeOptions={{not (and @onCreate (not @multiple))}}
      @onBlur={{@onBlur}}
      @onChange={{this.selectOrCreate}}
      @onClose={{@onClose}}
      @onFocus={{@onFocus}}
      @onInput={{@onInput}}
      @onKeydown={{this.onKeydown}}
      @onOpen={{@onOpen}}
      @optionKey={{@optionKey}}
      @optionPath={{@optionPath}}
      @options={{this.optionsArray}}
      @placeholder={{@placeholder}}
      @placeholderComponent={{@placeholderComponent}}
      @preventScroll={{@preventScroll}}
      @registerAPI={{this.handleRegisterAPI}}
      @renderInPlace={{@renderInPlace}}
      @scrollTo={{@scrollTo}}
      @search={{perform this.searchAndSuggest}}
      @searchEnabled={{orElse @searchEnabled true}}
      @searchField={{@searchField}}
      @searchMessage={{@searchMessage}}
      @searchMessageComponent={{@searchMessageComponent}}
      @searchPlaceholder={{@searchPlaceholder}}
      @selected={{@selected}}
      @selectedItemComponent={{@selectedItemComponent}}
      @selectedPath={{@selectedPath}}
      @size={{@size}}
      @tabindex={{@tabindex}}
      @triggerClass={{@triggerClass}}
      @renderAll={{@renderAll}}
      @triggerComponent={{orElse
        @triggerComponent
        (if
          (or (not @onCreate) @multiple)
          undefined
          (component
            BvSelectCreateSingleTrigger
            value=@value
            class=@triggerClass
            fullWidth=@fill
            autoFocus=@triggerAutoFocus
            disabled=@disabled
            onClear=(if @allowClear this.onClear)
          )
        )
      }}
      @triggerId={{@triggerId}}
      @triggerRole={{@triggerRole}}
      @typeAheadMatcher={{@typeAheadMatcher}}
      @verticalPosition={{@verticalPosition}}
      @warning={{@warning}}
      @inputClasses={{@inputClasses}}
      @dataName={{@dataName}}
      ...attributes
      as |option select|
    >
      {{#if option.__isSuggestion__}}
        <CreateOption @term={{select.searchText}} />
      {{else}}
        {{yield option select}}
      {{/if}}
    </this.selectComponent>
  </template>
  @service intl!: IntlService;

  select?: Select;

  matcher: MatcherFn = this.args.matcher ?? defaultMatcher;

  get selectComponent() {
    return ensureSafeComponent(
      this.args.multiple ? BvSelectMultiComponent : BvSelectComponent,
      this
    );
  }

  // Getters & CPs
  // ~~~~~~

  get optionsArray() {
    const { options, infiniteScroll } = this.args;
    // Because we use ember-infinity for infinite scrolling
    // we don't want to change the type of the response
    if (infiniteScroll) {
      return options;
    }
    if (!options) {
      return [];
    }
    if (isThenable(options as Promise<SelectedOption[]>)) {
      return (options as Promise<SelectedOption[]>).then((value) => value.slice());
    } else {
      return (options as SelectedOption[]).slice();
    }
  }

  // Lifecycle hooks
  // ~~~~~~

  // Actions
  // ~~~~~~

  @action
  handleRegisterAPI(select: Select) {
    this.select = select;
    this.args.registerAPI?.(select);
  }

  @action
  onKeydown(select: Select, event: KeyboardEvent) {
    if (event.key === 'Enter' && select.results.length === 1) {
      this.selectOrCreate(this.args.multiple ? select.results : select.results[0], select, event);
      select.actions.close();

      return false;
    }

    return this.args.onKeydown?.(select, event);
  }

  @action
  onClear(e: MouseEvent) {
    e.stopPropagation();
    this.select?.actions.select(null);
    if (e.type === 'touchstart') {
      return false;
    }
    return;
  }

  searchAndSuggest = task(async (term: string | undefined, select: Select) => {
    try {
      if (!term || term.length === 0) {
        return this.args.options;
      }
      const searchResults = (await this.args.search?.(term, select)) ?? this.args.options;
      const isInfinityModel = searchResults instanceof InfinityModel;
      const options = this.addCreateOption(
        term,
        isInfinityModel
          ? ((searchResults as unknown as InfinityModel).content as O[]).slice()
          : (searchResults as O[])
      );

      if (isInfinityModel) {
        (searchResults as unknown as InfinityModel).setObjects(options);
        return searchResults;
      }
      return options;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return this.args.options;
    }
  });

  @action
  selectOrCreate(
    selection: SelectedOption<O> | SelectedOption<O>[],
    select: Select,
    e: MouseEvent | KeyboardEvent
  ) {
    const suggestion = this.args.multiple
      ? (selection as (unknown | SuggestionOption)[]).filter(
          (option) => (option as SuggestionOption).__isSuggestion__
        )[0]
      : selection && (selection as SuggestionOption).__isSuggestion__
        ? selection
        : undefined;

    if (suggestion) {
      this.args.onCreate?.((suggestion as SuggestionOption).__value__, select, e);
    } else {
      this.args.onChange(selection, select, e);
    }
  }

  // Methods
  // ~~~~~~

  shouldShowCreateOption(term: string, options: O[] = []) {
    return this.args.onCreate && (this.args.showCreateWhen?.(term, options) ?? true);
  }

  addCreateOption(term: string, results: O[]): SelectedOption<O>[] {
    if (this.shouldShowCreateOption(term, results)) {
      if (this.args.showCreatePosition === 'bottom') {
        return [...(results ?? []), this.buildSuggestionForTerm(term)];
      } else {
        return [this.buildSuggestionForTerm(term), ...(results ?? [])];
      }
    }
    return [...results];
  }

  filter(options: unknown[], searchText: string) {
    let matcher: MatcherFn;
    if (this.args.searchField) {
      matcher = (option: unknown, text: string) =>
        this.matcher(get(option, this.args.searchField!), text);
    } else {
      matcher = (option: unknown, text: string) => this.matcher(option, text);
    }
    return filterOptions(options || [], searchText, matcher);
  }

  buildSuggestionForTerm(term: string): SuggestionOption {
    return {
      __isSuggestion__: true,
      __value__: term,
      text: this.buildSuggestionLabel(term)
    };
  }

  buildSuggestionLabel(term: string) {
    return this.args.buildSuggestion?.(term) ?? `${this.intl.t('add')} "${term}"...`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelectCreate: typeof BvSelectCreate;
    'bv-select-create': typeof BvSelectCreate;
  }
}
